import React from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Modal from '../../../components/Modal/Modal';
import { AppButton } from '../../../components/UI';

const TONES = {
  success: { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  muted: { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
  danger: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
};

const Stat = ({ icon, label, value, tone }) => (
  <Box sx={{ p: 1.5, borderRadius: '12px', border: `1px solid ${TONES[tone].border}`, bgcolor: TONES[tone].bg }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: TONES[tone].color, '& svg': { fontSize: 17 } }}>
      {icon}
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
    </Box>
    <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, mt: 0.5 }}>{value}</Typography>
  </Box>
);

const Section = ({ title, tone, items, render }) =>
  items.length > 0 && (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 1.5 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: TONES[tone].color, mb: 1 }}>
        {title} ({items.length})
      </Typography>
      <Box sx={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {items.slice(0, 30).map(render)}
        {items.length > 30 && <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8' }}>…et {items.length - 30} de plus</Typography>}
      </Box>
    </Box>
  );

const FileRow = ({ label, sub, subColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <DescriptionOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8', mt: 0.2 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: '0.82rem', color: '#0f172a' }}>{label}</Typography>
      {sub && <Typography sx={{ fontSize: '0.72rem', color: subColor }}>{sub}</Typography>}
    </Box>
  </Box>
);

const IngestReport = ({ report, onClose }) => {
  if (!report) return null;
  const { indexed, skipped, failed, results } = report;
  const byStatus = (s) => results.filter((r) => r.status === s);

  let headline = 'Aucun nouveau CV trouvé dans la boîte mail';
  if (failed > 0 && indexed === 0) headline = `${failed} CV n'ont pas pu être traités`;
  else if (indexed > 0) headline = `${indexed} nouveau${indexed > 1 ? 'x' : ''} candidat${indexed > 1 ? 's' : ''} ajouté${indexed > 1 ? 's' : ''}`;
  else if (skipped > 0) headline = 'Vivier à jour : aucun nouveau CV';

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AutoAwesomeOutlinedIcon />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Récupération terminée</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{headline}</Typography>
      </Box>
    </Box>
  );

  return (
    <Modal open onClose={onClose} title={title} maxWidth="sm" actions={<AppButton onClick={onClose}>Fermer</AppButton>}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25, mb: 2 }}>
        <Stat icon={<CheckCircleOutlinedIcon />} label="Indexés" value={indexed} tone="success" />
        <Stat icon={<ContentCopyOutlinedIcon />} label="Doublons" value={skipped} tone="muted" />
        <Stat icon={<ErrorOutlinedIcon />} label="Échecs" value={failed} tone="danger" />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Section title="Nouveaux candidats" tone="success" items={byStatus('indexed')} render={(r, i) => <FileRow key={i} label={r.full_name || r.filename || 'CV'} />} />
        <Section title="Échecs" tone="danger" items={byStatus('failed')} render={(r, i) => <FileRow key={i} label={r.filename || 'CV'} sub={r.error} subColor="#dc2626" />} />
        <Section title="Doublons ignorés" tone="muted" items={byStatus('skipped')} render={(r, i) => <FileRow key={i} label={r.filename || 'CV'} />} />
      </Box>
      <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8', mt: 1.5 }}>La liste des candidats a été mise à jour automatiquement.</Typography>
    </Modal>
  );
};

export default IngestReport;
